import { Box, BoxProps, Flex, Icon, IconButton, Portal, Text, VStack, useDisclosure, useMediaQuery } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building, Check, ChevronsUpDown, LogOut, Menu, X } from 'lucide-react';
import { PropsWithChildren, useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate, useRevalidator, useRouteLoaderData } from 'react-router-dom';
import logoFull from '../assets/logo-light-full.svg';
import logoIcon from '../assets/logo-light-icon.svg';
import { LoginResponseDto, OrgSelectorRequestDto, api } from '../lib/api';
import { Navigation } from './navigation';
import { NavigationItem } from './navigation-item';
import { Avatar } from './ui/avatar';
import { DialogBody, DialogCloseTrigger, DialogContent, DialogHeader, DialogRoot, DialogTitle } from './ui/dialog';
import { DrawerContent, DrawerRoot } from './ui/drawer';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from './ui/menu';

export interface MenuItem {
    url: string;
    label: string;
}

export interface MenuItems {
    [index: string]: MenuItem | undefined;
}

interface SidebarProps extends BoxProps {
    onClose: () => void;
    menuItems: MenuItems;
    isExpanded?: boolean;
    onExpand?: () => void;
    onCollapse?: () => void;
}

export interface MenuItem {
    url: string;
    label: string;
}

export const isPathMatch = (path: string, itemUrl: string): boolean => {
    if (itemUrl === '/dashboard') {
        return path === '/' || path.startsWith('/dashboard');
    }
    if (itemUrl.startsWith('/organization')) {
        return path.startsWith('/organization');
    }
    return path.startsWith(itemUrl);
};

const SidebarContent = ({ onClose, menuItems, isExpanded, onExpand, onCollapse, ...rest }: SidebarProps) => {
    const navigate = useNavigate();
    const { revalidate } = useRevalidator();
    const queryClient = useQueryClient();
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const session = useRouteLoaderData('root') as LoginResponseDto;
    const [, setSelectedOrg] = useState<string | null>(null);
    const [rememberedActiveMenuItem, setRememberedActiveMenuItem] = useState<string>();
    const location = useLocation();

    const activeMenuItem = useMemo(() => {
        const path = location.pathname;
        const directlyActiveItem = Object.values(menuItems).find((item) => item && isPathMatch(path, item.url));

        return directlyActiveItem?.url || rememberedActiveMenuItem;
    }, [location.pathname, menuItems, rememberedActiveMenuItem]);

    useEffect(() => {
        const path = location.pathname;
        const directlyActiveItem = Object.values(menuItems).find((item) => item && isPathMatch(path, item.url));

        if (directlyActiveItem) {
            setRememberedActiveMenuItem(directlyActiveItem.url);
        }
    }, [location.pathname, menuItems]);

    const { mutate } = useMutation<unknown, unknown>({
        mutationFn: async () => {
            const response = await api.authentication.logout();
            if (response.status !== 200) {
                throw new Error('logout failed');
            }
        },
        onSuccess: () => {
            queryClient.clear();
            navigate('/login');
        },
    });

    const { mutate: mutateOrg } = useMutation<LoginResponseDto | null, unknown, OrgSelectorRequestDto>({
        mutationFn: async (body) => {
            const { data, status } = await api.authentication.selector(body);
            if (status !== 200) {
                throw new Error();
            }
            return data;
        },
        onSuccess: async () => {
            queryClient.clear();
            setSwitcherOpen(false);
            navigate('/', { replace: true });
            revalidate();
            setSelectedOrg(null);
        },
    });

    const handleOrgSelect = (id: string, name: string) => {
        if (id === session.activeOrg?.id) {
            setSwitcherOpen(false);
            return;
        }
        mutateOrg({ id: id });
        setSelectedOrg(name);
    };

    const textOverflowEllipses = {
        maxW: '128px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    };

    return (
        <>
            <Box
                w={{ base: 'full', md: isExpanded ? '240px' : '70px' }}
                pos="fixed"
                ml={isExpanded ? 1 : 0}
                mt={isExpanded ? 0 : 4}
                h="full"
                {...rest}>
                <Flex pos="relative" flexDirection="column" h="full">
                    <Flex
                        h="20"
                        pl={{ base: '0px', md: '20px' }}
                        pt="22px"
                        pb="18px"
                        gap="12px"
                        alignItems="center"
                        mx={{ base: 8, md: 0 }}
                        justifyContent={{ base: 'space-between', md: 'flex-start' }}>
                        <Flex gap={isExpanded ? '90px' : '16px'} alignItems="center" flexDirection={isExpanded ? 'row' : 'column'}>
                            {isExpanded && (
                                <Link to="/home">
                                    <img src={logoFull} alt="Musa EHR Logo" />
                                </Link>
                            )}
                            {!isExpanded && (
                                <Link to="/home">
                                    <img src={logoIcon} alt="Musa EHR Logo" />
                                </Link>
                            )}
                            {onCollapse && onExpand && (
                                <IconButton
                                    color="musa.light.label"
                                    variant="subtle"
                                    bg="transparent"
                                    aria-label="toggle menu"
                                    boxSize="20px"
                                    minW="32px"
                                    onClick={() => {
                                        if (isExpanded) {
                                            onCollapse();
                                        } else {
                                            onExpand();
                                        }
                                    }}>
                                    <Menu />
                                </IconButton>
                            )}
                        </Flex>
                        <IconButton
                            color="musa.light.label"
                            variant="subtle"
                            bg="transparent"
                            display={{ base: 'flex', md: 'none' }}
                            aria-label="close menu"
                            onClick={onClose}>
                            <X />
                        </IconButton>
                    </Flex>
                    <VStack
                        h="100%"
                        justifyContent="space-between"
                        p="3"
                        pl={isExpanded ? 3 : 4}
                        pt={isExpanded ? 5 : 0}
                        mb={isExpanded ? 0 : 4}
                        overflowY="auto">
                        <Box w="100%" data-testid="sidebar-menu">
                            {Object.entries(menuItems).map(
                                ([key, item]) =>
                                    item && (
                                        <NavigationItem
                                            hideLabel={!isExpanded}
                                            key={key}
                                            href={item.url}
                                            icon={key}
                                            label={item.label}
                                            onClick={onClose}
                                            activeMenuItem={activeMenuItem}
                                        />
                                    ),
                            )}
                        </Box>
                        <Flex justifyContent="space-between" alignItems="center" width="100%" gap="3">
                            {isExpanded && <Avatar size="sm" name={session.displayName} />}
                            {isExpanded && (
                                <Box>
                                    <Text color="#FFFFFF" fontSize="13px" {...textOverflowEllipses}>
                                        {session.displayName}
                                    </Text>
                                    <Text color="musa.light.label" fontSize="11px" {...textOverflowEllipses}>
                                        {session.activeOrg?.name}
                                    </Text>
                                </Box>
                            )}
                            <MenuRoot>
                                <MenuTrigger asChild>
                                    <IconButton
                                        variant="plain"
                                        border="none"
                                        height="100%"
                                        bg="transparent"
                                        color="musa.light.label"
                                        aria-label="toggle user menu"
                                        _hover={{ cursor: 'pointer' }}
                                        _active={{ backgroundColor: 'transparent' }}>
                                        {isExpanded ? <ChevronsUpDown /> : <Avatar size="sm" name={session.displayName} />}
                                    </IconButton>
                                </MenuTrigger>
                                <Portal>
                                    <MenuContent zIndex={99999} data-testid="user-menu">
                                        {session.organizations.length > 1 && (
                                            <MenuItem value="switch" onClick={() => setSwitcherOpen(true)}>
                                                <Building />
                                                Switch Organization
                                            </MenuItem>
                                        )}
                                        <MenuItem value="logout" onClick={() => mutate()}>
                                            <LogOut />
                                            Logout
                                        </MenuItem>
                                    </MenuContent>
                                </Portal>
                            </MenuRoot>
                        </Flex>
                    </VStack>
                </Flex>
            </Box>
            <DialogRoot
                open={switcherOpen}
                size="md"
                onOpenChange={(value) => {
                    setSwitcherOpen(value.open);
                }}>
                <DialogContent gap="0">
                    <DialogHeader>
                        <DialogTitle>Select Organization</DialogTitle>
                        <DialogCloseTrigger />
                    </DialogHeader>
                    <DialogBody p="0" m="0">
                        <VStack gap="0" alignItems="flex-start">
                            {session.organizations
                                ?.sort((a) => (a.id === session.activeOrg?.id ? -1 : 1))
                                ?.map((org, index) => {
                                    const isActiveOrg = org.id === session.activeOrg?.id;

                                    return (
                                        <Flex
                                            key={org.id}
                                            alignItems="center"
                                            height="14"
                                            width="100%"
                                            borderTop={index > 0 ? '1px' : undefined}
                                            borderColor={index > 0 ? '#EAE9EB' : undefined}
                                            _hover={{ cursor: 'pointer' }}>
                                            <Flex
                                                px={isActiveOrg ? '6' : '4'}
                                                py="2"
                                                gap="2"
                                                onClick={() => handleOrgSelect(org.id, org.name)}
                                                width="100%">
                                                {isActiveOrg && (
                                                    <Icon boxSize="6">
                                                        <Check />
                                                    </Icon>
                                                )}
                                                <Text alignItems="center" pl={isActiveOrg ? undefined : '10'}>
                                                    {org.name}
                                                </Text>
                                            </Flex>
                                        </Flex>
                                    );
                                })}
                        </VStack>
                    </DialogBody>
                </DialogContent>
            </DialogRoot>
        </>
    );
};

export const Sidebar = ({ children, menuItems }: PropsWithChildren<{ menuItems: MenuItems }>) => {
    const { open, onOpen, onClose } = useDisclosure();
    const { open: isExpanded, onOpen: onExpand, onClose: onCollapse } = useDisclosure({ defaultOpen: true });
    const [isMobile] = useMediaQuery(['(max-width: 768px)'], { fallback: [true] });

    return (
        <Box h="100%" flexGrow="1" bg={{ base: 'musa.light.100', _dark: 'musa.dark.100' }}>
            <SidebarContent
                menuItems={menuItems}
                onClose={() => onClose}
                display={{ base: 'none', md: 'block' }}
                onCollapse={onCollapse}
                isExpanded={isExpanded}
                onExpand={onExpand}
                data-testid="sidebar"
            />
            <DrawerRoot open={open && isMobile} placement="start" size="full">
                <DrawerContent bg={{ base: 'musa.light.100', _dark: 'musa.dark.100' }} data-testid="mobile-sidebar">
                    <SidebarContent menuItems={menuItems} onClose={onClose} isExpanded />
                </DrawerContent>
            </DrawerRoot>
            <Navigation onOpen={onOpen} isOpen={open} display={{ base: 'flex', md: 'none' }} />
            <Box
                transition={isMobile ? undefined : '1s ease'}
                ml={{ base: 0, md: isExpanded ? '240px' : '70px' }}
                py={{ base: 0, md: 2 }}
                pr={{ base: 0, md: 2 }}
                height="100%"
                display="flex"
                flexDirection="column">
                {children}
            </Box>
        </Box>
    );
};
